import React, { useState, useEffect } from 'react';
import { usePlayer } from '../../context/PlayerContext';
import { X, Save } from 'lucide-react';
import PropTypes from 'prop-types';
import { convertBaseballInningsToDecimal } from '../../utils/calculations';

const EditGameModal = ({ game, onClose, onSuccess }) => {
  const { updateGame, playerData, isLoading } = usePlayer();
  const [gameData, setGameData] = useState({
    date: '',
    opponent: '',
    league: '',
    // Bat information
    batMaker: '',
    batWoodType: '',
    batKnobType: 'Standard',
    batWeight: 30,
    batLength: 33,
    batModel: '',
    // Batting stats
    atBats: 0,
    hits: 0,
    doubles: 0,
    triples: 0,
    homeRuns: 0,
    runs: 0,
    rbi: 0,
    walks: 0,
    strikeouts: 0,
    hitByPitch: 0,
    // Pitching stats
    didPitch: false,
    inningsPitched: '',
    pitchingStrikeouts: 0,
    hitsAllowed: 0,
    walksAllowed: 0,
    earnedRuns: 0,
    pitchingResult: 'No Decision'
  });

  // Initialize form with game data
  useEffect(() => {
    if (game) {
      const battingStats = game.battingStats || {};
      const pitchingStats = game.pitchingStats || {};
      const batInfo = game.batInfo || {};
      
      setGameData({
        date: game.date ? new Date(game.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        opponent: game.opponent || '',
        league: game.league || (playerData?.leagues?.length > 0 ? playerData.leagues[0] : ''),
        // Bat information
        batMaker: batInfo.maker || '',
        batWoodType: batInfo.woodType || '',
        batKnobType: batInfo.knobType || 'Standard',
        batWeight: batInfo.weight || 30,
        batLength: batInfo.length || 33,
        batModel: batInfo.model || '',
        // Batting stats
        atBats: battingStats.atBats || 0,
        hits: battingStats.hits || 0,
        doubles: battingStats.doubles || 0,
        triples: battingStats.triples || 0,
        homeRuns: battingStats.homeRuns || 0,
        runs: battingStats.runs || 0,
        rbi: battingStats.rbi || 0,
        walks: battingStats.walks || 0,
        strikeouts: battingStats.strikeouts || 0,
        hitByPitch: battingStats.hitByPitch || 0,
        // Pitching stats
        didPitch: !!pitchingStats.inningsPitched,
        inningsPitched: pitchingStats.inningsPitched || '',
        pitchingStrikeouts: pitchingStats.strikeouts || 0,
        hitsAllowed: pitchingStats.hits || 0,
        walksAllowed: pitchingStats.walks || 0,
        earnedRuns: pitchingStats.earnedRuns || 0,
        pitchingResult: pitchingStats.result || 'No Decision'
      });
    }
  }, [game, playerData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setGameData({
      ...gameData,
      [name]: newValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formattedGame = {
      date: new Date(gameData.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      opponent: gameData.opponent,
      league: gameData.league,
      // Add bat information
      batInfo: {
        maker: gameData.batMaker,
        woodType: gameData.batWoodType,
        knobType: gameData.batKnobType,
        weight: parseFloat(gameData.batWeight),
        length: parseFloat(gameData.batLength),
        model: gameData.batModel
      },
      battingStats: {
        atBats: Number(gameData.atBats),
        hits: Number(gameData.hits),
        doubles: Number(gameData.doubles),
        triples: Number(gameData.triples),
        homeRuns: Number(gameData.homeRuns),
        runs: Number(gameData.runs),
        rbi: Number(gameData.rbi),
        walks: Number(gameData.walks),
        strikeouts: Number(gameData.strikeouts),
        hitByPitch: Number(gameData.hitByPitch)
      }
    };
    
    // Add pitching stats if applicable
    if (gameData.didPitch) {
      formattedGame.pitchingStats = {
        inningsPitched: convertBaseballInningsToDecimal(gameData.inningsPitched),
        strikeouts: Number(gameData.pitchingStrikeouts),
        hits: Number(gameData.hitsAllowed),
        walks: Number(gameData.walksAllowed),
        earnedRuns: Number(gameData.earnedRuns),
        result: gameData.pitchingResult
      };
    }
    
    // Preserve the seasonId if it exists
    if (game.seasonId) {
      formattedGame.seasonId = game.seasonId;
    }
    
    // Update the game
    const success = await updateGame(game.id, formattedGame);
    
    if (success) {
      onSuccess();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div className="bg-white dark:bg-dark-200 rounded-lg shadow-lg max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold dark:text-darkText-primary">Edit Game</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <X size={24} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Game Info Section */}
          <div className="grid grid-cols-2 gap-4">
            {/* Date Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                Date
              </label>
              <input 
                type="date" 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                name="date"
                value={gameData.date}
                onChange={handleChange}
                required
              />
            </div>
            
            {/* Opponent Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                Opponent
              </label>
              <input 
                type="text" 
                className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                placeholder="Team name"
                name="opponent"
                value={gameData.opponent}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          
          {/* League Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
              League
            </label>
            <select 
              className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
              name="league"
              value={gameData.league}
              onChange={handleChange}
            >
              {/* Map through player's leagues or show a default if none exist */}
              {playerData?.leagues?.length > 0 ? (
                playerData.leagues.map((league, index) => (
                  <option key={index} value={league}>{league}</option>
                ))
              ) : (
                <option value="">No leagues available</option>
              )}
            </select>
          </div>
          
          {/* Bat Information Section */}
          <div className="border-b border-gray-200 dark:border-dark-400 pb-4">
            <h3 className="text-lg font-medium dark:text-darkText-primary mb-3">Bat Information</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-3">
              {/* Bat Maker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                  Bat Maker
                </label>
                <select 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                  name="batMaker"
                  value={gameData.batMaker}
                  onChange={handleChange}
                >
                  <option value="">Select Bat Maker</option>
                  <option value="Marucci">Marucci</option>
                  <option value="Victus">Victus</option>
                  <option value="Louisville Slugger">Louisville Slugger</option>
                  <option value="Mizuno">Mizuno</option>
                  <option value="Rawlings">Rawlings</option>
                  <option value="DeMarini">DeMarini</option>
                  <option value="Old Hickory">Old Hickory</option>
                  <option value="Beaver Bats">Beaver Bats</option>
                  <option value="Warstic">Warstic</option>
                  <option value="Nike">Nike</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              {/* Wood Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                  Wood Type
                </label>
                <select 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                  name="batWoodType"
                  value={gameData.batWoodType}
                  onChange={handleChange}
                >
                  <option value="">Select Wood Type</option>
                  <option value="Maple">Maple</option>
                  <option value="Hickory">Hickory</option>
                  <option value="Bamboo">Bamboo</option>
                  <option value="Ash">Ash</option>
                  <option value="Birch">Birch</option>
                  <option value="Composite">Composite</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 mb-3">
              {/* Knob Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                  Knob Type
                </label>
                <select 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                  name="batKnobType"
                  value={gameData.batKnobType}
                  onChange={handleChange}
                >
                  <option value="Standard">Standard</option>
                  <option value="Flared">Flared</option>
                  <option value="Axe">Axe</option>
                  <option value="Thin">Thin</option>
                </select>
              </div>
              
              {/* Bat Model */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                  Bat Model (Optional)
                </label>
                <input 
                  type="text" 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                  placeholder="e.g. AP5, 271, etc."
                  name="batModel"
                  value={gameData.batModel}
                  onChange={handleChange}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Bat Length */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                  Bat Length: {gameData.batLength} inches
                </label>
                <input 
                  type="range" 
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-dark-400"
                  min="20"
                  max="40"
                  step="0.5"
                  name="batLength"
                  value={gameData.batLength}
                  onChange={handleChange}
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-darkText-tertiary mt-1">
                  <span>20"</span>
                  <span>30"</span>
                  <span>40"</span>
                </div>
              </div>
              
              {/* Bat Weight */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">
                  Bat Weight: {gameData.batWeight} oz
                </label>
                <input 
                  type="range" 
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-dark-400"
                  min="20"
                  max="45"
                  step="0.5"
                  name="batWeight"
                  value={gameData.batWeight}
                  onChange={handleChange}
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-darkText-tertiary mt-1">
                  <span>20oz</span>
                  <span>32.5oz</span>
                  <span>45oz</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Batting Stats Section */}
          <div className="border-b border-gray-200 dark:border-dark-400 pb-2">
            <h3 className="text-lg font-medium dark:text-darkText-primary mb-2">Batting Stats</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">At Bats</label>
                <input 
                  type="number" 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                  min="0"
                  name="atBats"
                  value={gameData.atBats}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Hits</label>
                <input 
                  type="number" 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                  min="0"
                  name="hits"
                  value={gameData.hits}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">2B</label>
                <input 
                  type="number" 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                  min="0"
                  name="doubles"
                  value={gameData.doubles}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">3B</label>
                <input 
                  type="number" 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                  min="0"
                  name="triples"
                  value={gameData.triples}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">HR</label>
                <input 
                  type="number" 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                  min="0"
                  name="homeRuns"
                  value={gameData.homeRuns}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Runs</label>
                <input 
                  type="number" 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                  min="0"
                  name="runs"
                  value={gameData.runs}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">RBI</label>
                <input 
                  type="number" 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                  min="0"
                  name="rbi"
                  value={gameData.rbi}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">BB</label>
                <input 
                  type="number" 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                  min="0"
                  name="walks"
                  value={gameData.walks}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">K</label>
                <input 
                  type="number" 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                  min="0"
                  name="strikeouts"
                  value={gameData.strikeouts}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">HBP</label>
                <input 
                  type="number" 
                  className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                  min="0"
                  name="hitByPitch"
                  value={gameData.hitByPitch}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
          
          {/* Pitching Stats Section */}
          <div>
            <div className="flex items-center mb-2">
              <h3 className="text-lg font-medium dark:text-darkText-primary">Pitching Stats</h3>
              <div className="ml-2">
                <input 
                  type="checkbox" 
                  id="didPitch" 
                  className="mr-1 dark:bg-dark-300 dark:border-dark-400"
                  name="didPitch"
                  checked={gameData.didPitch}
                  onChange={handleChange}
                />
                <label htmlFor="didPitch" className="text-sm dark:text-darkText-primary">I pitched in this game</label>
              </div>
            </div>
            
            {gameData.didPitch && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Innings Pitched</label>
                    <input 
                      type="text" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      placeholder="e.g. 6.1"
                      name="inningsPitched"
                      value={gameData.inningsPitched}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Strikeouts</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      min="0"
                      name="pitchingStrikeouts"
                      value={gameData.pitchingStrikeouts}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Hits Allowed</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      min="0"
                      name="hitsAllowed"
                      value={gameData.hitsAllowed}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Walks</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      min="0"
                      name="walksAllowed"
                      value={gameData.walksAllowed}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Earned Runs</label>
                    <input 
                      type="number" 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md" 
                      min="0"
                      name="earnedRuns"
                      value={gameData.earnedRuns}
                      onChange={handleChange}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-darkText-primary mb-1">Result</label>
                    <select 
                      className="w-full p-2 border border-gray-300 dark:border-dark-400 dark:bg-dark-300 dark:text-darkText-primary rounded-md"
                      name="pitchingResult"
                      value={gameData.pitchingResult}
                      onChange={handleChange}
                    >
                      <option value="Win">Win</option>
                      <option value="Loss">Loss</option>
                      <option value="No Decision">No Decision</option>
                    </select>
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Submit Button */}
          <div className="pt-4">
            <button 
              type="submit" 
              disabled={isLoading}
              className="w-full bg-blue-600 dark:bg-blue-700 text-white py-2 px-4 rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-t-2 border-b-2 border-white rounded-full mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

EditGameModal.propTypes = {
  game: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired
};

export default EditGameModal;
